import React, { useState,useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Edit, Plus, Search, Filter, Package, MapPin, Calendar, DollarSign, User, X, ChevronLeft, ChevronRight, PlaneTakeoff, Shield, Clock, Star, Send, Trash2 } from 'lucide-react';
import { parcelsAPI, feesAPI, applicationsAPI } from '../services/api';
import CreateParcelModal from '../components/CreateParcelModal';
import EditParcelForm from '../components/EditParcelForm';
import ParcelCardSleek from '../components/ParcelCardSleek';
import { useThemeColors } from '../theme/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const Parcels = () => {
  const { user } = useAuth();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [bidPrice, setBidPrice] = useState('');
  const [activeTab, setActiveTab] = useState('available');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    deliveryType: '',
    minWeight: '',
    maxWeight: '',
    minValue: '',
    maxValue: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Check for repost parameter and open create modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newPost = urlParams.get('action') === 'new';
    
    if (newPost) {
      setShowCreateModal(true);
      // Clean up URL parameter
      window.history.replaceState({}, '', '/parcels');
    }
  }, []);

  // Reset page when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, activeTab]);

  const [repostData, setRepostData] = useState(null);

  // Handle repost functionality
  const handleRepost = (data) => {
    setRepostData(data);
    setShowCreateModal(true);
  };
  // Update fee estimate when bidPrice changes
  useEffect(() => {
    if (selectedParcel && bidPrice && parseFloat(bidPrice) > 0 && bidPrice < selectedParcel.max_delivery_price) {
      const calculateBidFee = async () => {
        setIsLoadingFee(true);
        try {
          const response = await feesAPI.calculateFeeEstimate('apply_to_parcel', {
            weight_kg: parseFloat(selectedParcel.weight_kg),
            declared_value: parseFloat(selectedParcel.declared_value) || 0,
            guaranteed_delivery: selectedParcel.guaranteed_delivery,
            purchased: selectedParcel.purchased,
            purchase_price: parseFloat(selectedParcel.purchase_price) || 0,
            id: selectedParcel.id,
            proposal_price: parseFloat(bidPrice)
          });
          // console.log(response.data);
          
          setFeeEstimate(response.data.data);
          setIsLoadingFee(false);
        } catch (error) {
          console.error('Fee estimate error:', error);
          setIsLoadingFee(false);
        }
      };

      const timeoutId = setTimeout(calculateBidFee, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [bidPrice, selectedParcel]);

  const queryClient = useQueryClient();

  // Pagination helper functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Fetch available parcels
  const {
    data: availableParcels,
    isLoading: isLoadingAvailable,
    refetch: refetchAvailable,
  } = useQuery(
    ['availableParcels', searchTerm, filters, currentPage, itemsPerPage],
    () => parcelsAPI.getAvailableParcels({ 
      search: searchTerm, 
      ...filters,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage
    }),
    {
      enabled: activeTab === 'available',
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data || [];
        if (apiData.parcels.length > 0) {
          return apiData;
        }
        return [];
      },
      onSuccess: (data) => {
        const apiData = data || [];
        if (apiData.pagination?.total) {
          setTotalItems(apiData.pagination.total);
        } else if (apiData.parcels?.length) {
          setTotalItems(apiData.parcels.length);
        } else {
          setTotalItems(0);
        }
      }
    }
  );

  // Fetch user parcels
  const {
    data: userParcels,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useQuery(
    ['userParcels', activeTab, currentPage, itemsPerPage],
    () => parcelsAPI.getUserParcels(activeTab === 'my' ? '' : activeTab, {
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage
    }),
    {
      enabled: activeTab !== 'available',
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data || [];
        if (apiData.parcels.length > 0) {
          return apiData;
        }
        return [];
      },
      onSuccess: (data) => {
        const apiData = data || [];
        if (apiData.pagination?.total) {
          setTotalItems(apiData.pagination.total);
        } else if (apiData.parcels?.length) {
          setTotalItems(apiData.parcels.length);
        } else {
          setTotalItems(0);
        }
      }
    }
  );

  // Create parcel mutation
  const createParcelMutation = useMutation(parcelsAPI.createParcel, {
    onSuccess: () => {
      toast.success('Parcel created successfully!');
      setShowCreateModal(false);
      queryClient.invalidateQueries('userParcels');
      refetchUser();
    },
    onError: (error) => {
      console.log(error);
      
      toast.error(error.response?.data?.message || 'Failed to create parcel');
    },
  });

  // Edit parcel mutation
  const editParcelMutation = useMutation(
    ({ id, parcelData }) => parcelsAPI.updateParcel(id, parcelData), {
    onSuccess: () => {
      toast.success('Parcel updated successfully!');
      queryClient.invalidateQueries('userParcels');
      refetchUser();
      setShowEditModal(false);
      setSelectedParcel(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update parcel');
    },
  });

  // Cancel parcel mutation
  const cancelParcelMutation = useMutation(parcelsAPI.cancelParcel, {
    onSuccess: () => {
      toast.success('Parcel cancelled successfully!');
      queryClient.invalidateQueries('userParcels');
      refetchUser();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel parcel');
    },
  });

  // Delete parcel mutation (soft delete by updating status)
  const deleteParcelMutation = useMutation(
    (id) => parcelsAPI.deleteParcel(id), {
    onSuccess: () => {
      toast.success('Parcel deleted successfully!');
      queryClient.invalidateQueries('userParcels');
      refetchUser();
      setShowDeleteModal(false);
      setSelectedParcel(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete parcel');
    },
  });

  const handleCreateParcel = (parcelData) => {
    createParcelMutation.mutate(parcelData);
  };


  const handleEditClick = (parcel) => {
    if (parseInt(parcel.applications_count) !== 0) {
      toast.error('Cannot edit parcel with applications');
      return;
    }
    setSelectedParcel(parcel);
    setShowEditModal(true);
  };

  const handleCancelClick = (parcel) => {
    setSelectedParcel(parcel);
    setShowCancelModal(true);
  };

  const handleDeleteClick = (parcel) => {
    setSelectedParcel(parcel);
    setShowDeleteModal(true);
  };

  const handleViewDetails = async (parcelId) => {
    navigate(`/parcels/${parcelId}`);
  };

  const handleEditParcel = (parcel) => {
    
    setSelectedParcel(parcel);
    setShowEditModal(true);
  };

  const confirmCancelParcel = () => {
    if (selectedParcel) {
      cancelParcelMutation.mutate(selectedParcel.id);
      setShowCancelModal(false);
      setSelectedParcel(null);
    }
  };

  const confirmDeleteParcel = () => {
    if (selectedParcel) {
      // Ensure ID is a number
      const parcelId = parseInt(selectedParcel.id, 10);
      deleteParcelMutation.mutate(parcelId);
    }
  };

  const confirmEditParcel = (formData) => {
    if (selectedParcel) {
      console.log(formData);
      console.log(formData.id);
      
      // The formData is already properly formatted from EditParcelForm
      // Pass id and data as variables object for useMutation
      editParcelMutation.mutate({ 
        id: formData.id, 
        parcelData: formData 
      });
    }
  };

  const confirmApplyToParcel = async () => {
    // Validate bid price
    if (!bidPrice || parseFloat(bidPrice) < 50) {
      toast.error('Please enter a valid delivery price (minimum ETB 50)');
      return;
    }
    
    if (parseFloat(bidPrice) > selectedParcel.max_delivery_price) {
      toast.error(`Price cannot exceed ETB ${selectedParcel.max_delivery_price}`);
      return;
    }
    
    setIsApplying(true);
    try {
      // Create application with bid price
      await applicationsAPI.createApplication({
        target_type: 'parcel',
        target_id: selectedParcel.id,
        message: `I would like to deliver this ${selectedParcel.category} parcel from ${selectedParcel.pickup} to ${selectedParcel.dropoff}. Weight: ${selectedParcel.weight_kg}kg. My proposed delivery price: ETB ${bidPrice}.`,
        proposed_price: parseFloat(bidPrice)
      });
      
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      setSelectedParcel(null);
      setBidPrice(''); // Reset bid price
      
      // Refresh parcels list
      if (activeTab === 'available') {
        refetchAvailable();
      } else {
        refetchUser();
      }
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setIsApplying(false);
    }
  };

  const isLoading = activeTab === 'available' ? isLoadingAvailable : isLoadingUser;
  const parcels = activeTab === 'available' ? availableParcels?.parcels : userParcels?.parcels;

  // Helper function to check if any filters are active
  const hasActiveFilters = () => {
    return Boolean(
      searchTerm ||
      filters.category ||
      filters.deliveryType ||
      filters.minWeight ||
      filters.maxWeight ||
      filters.minValue ||
      filters.maxValue ||
      filters.status
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
      <SEO 
        title="Parcels" 
        description="Browse and manage parcels for shipping. Find parcels to deliver or post your own packages on TripTrot"
        keywords={['parcel shipping', 'package delivery', 'find parcels', 'post parcels', 'shipping services']}
        canonical="/parcels"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Parcels",
          "description": "Browse available parcels and manage shipping listings"
        }}
      />
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 
              className="text-3xl font-bold" 
              style={{ color: colors.text.primary }}
            >
              Parcels
            </h1>
            <p 
              className="mt-2" 
              style={{ color: colors.text.secondary }}
            >
              {activeTab === 'available' 
                ? 'Browse available parcels to deliver' 
                : 'Manage your parcel listings'}
            </p>
          </div>
          {activeTab !== 'available' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 rounded-lg transition-colors"
              style={{
                backgroundColor: colors.primary[600],
                color: 'white'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[700];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary[600];
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Post Parcel
            </button>
          )}
        </div>
      </div>

      {/* Sleek Tabs */}
      <div className="mb-8">
        {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
        <div className="sm:hidden">
          <div 
            className="rounded-2xl p-2 space-y-1 border"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'available', label: 'Available', icon: Package },
              { key: 'my', label: 'My Parcels', icon: User },
              // { key: 'posted', label: 'Posted', icon: MapPin },
              // { key: 'cancelled', label: 'Cancelled', icon: X }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop version */}
        <div className="hidden sm:block">
          <div 
            className="rounded-2xl p-2 flex flex-wrap gap-2 items-center justify-around"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'available', label: 'Available', icon: Package },
              { key: 'my', label: 'My Parcels', icon: User },
              // { key: 'posted', label: 'Posted', icon: MapPin },
              // { key: 'cancelled', label: 'Cancelled', icon: X }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        {/* Main Search Card */}
        <div 
          className="rounded-2xl shadow-xl transition-all duration-300 overflow-hidden"
          style={{ 
            backgroundColor: colors.card, 
            borderColor: colors.border 
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary[300];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
          }}
        >
          {/* Top accent bar */}
          <div className="h-1" />
          
          {/* Content */}
          <div className="p-6">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" 
                  style={{ color: colors.text.secondary }}
                />
                <input
                  type="text"
                  placeholder="Search parcels..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 w-full rounded-xl transition-all duration-200 border"
                  style={{
                    backgroundColor: colors.neutral[50],
                    borderColor: colors.border,
                    color: colors.text.primary
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.primary[500];
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center px-6 py-3 rounded-xl transition-all duration-200 font-medium border ${
                  showFilters 
                    ? 'shadow-lg' 
                    : ''
                }`}
                style={{
                  backgroundColor: showFilters ? colors.primary[600] : colors.neutral[50],
                  color: showFilters ? 'white' : colors.text.secondary,
                  borderColor: showFilters ? colors.primary[600] : colors.border
                }}
                onMouseEnter={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                  }
                }}
              >
                <Filter className="h-5 w-5 mr-2" />
                Filters
                {(filters.category || filters.deliveryType || filters.minWeight || filters.maxWeight || filters.minValue || filters.maxValue || filters.status) && (
                  <span 
                    className="ml-2 px-2 py-0.5 text-xs rounded-full"
                    style={{
                      backgroundColor: colors.primary[600],
                      color: 'white'
                    }}
                  >
                    {[filters.category, filters.deliveryType, filters.minWeight, filters.maxWeight, filters.minValue, filters.maxValue, filters.status].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Filter Row */}
            {showFilters && activeTab === 'available' && (
              <div className="mt-6 pt-6 animate-fadeIn" style={{ borderTop: `1px solid ${colors.border}` }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Category Filter */}
                  <div className="relative">
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl appearance-none cursor-pointer transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">All Categories</option>
                      <option value="documents">Documents</option>
                      <option value="electronics">Electronics</option>
                      <option value="clothing">Clothing</option>
                      <option value="food">Food</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg 
                        className="w-4 h-4" 
                        style={{ color: colors.text.secondary }}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Delivery Type Filter */}
                  <div className="relative">
                    <select
                      value={filters.deliveryType}
                      onChange={(e) => setFilters({ ...filters, deliveryType: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl appearance-none cursor-pointer transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">All Delivery Types</option>
                      <option value="standard">Standard Delivery</option>
                      <option value="guaranteed">Guaranteed Delivery</option>
                      <option value="purchased">Purchased Items</option>
                      <option value="both">Both Guaranteed & Purchased</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg 
                        className="w-4 h-4" 
                        style={{ color: colors.text.secondary }}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Min Weight */}
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Min Weight (kg)"
                      value={filters.minWeight}
                      onChange={(e) => setFilters({ ...filters, minWeight: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Max Weight */}
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Max Weight (kg)"
                      value={filters.maxWeight}
                      onChange={(e) => setFilters({ ...filters, maxWeight: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Min Value */}
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Min Value (ETB)"
                      value={filters.minValue}
                      onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Max Value */}
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Max Value (ETB)"
                      value={filters.maxValue}
                      onChange={(e) => setFilters({ ...filters, maxValue: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="relative">
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl appearance-none cursor-pointer transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">All Statuses</option>
                      <option value="posted">Posted</option>
                      <option value="in_transit">In Transit</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg 
                        className="w-4 h-4" 
                        style={{ color: colors.text.secondary }}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  <div className="relative">
                    <button
                      onClick={() => setFilters({ 
                        category: '', 
                        deliveryType: '',
                        minWeight: '', 
                        maxWeight: '', 
                        minValue: '', 
                        maxValue: '',
                        status: ''
                      })}
                      className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl transition-all duration-200"
                      style={{
                        color: colors.error[600],
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.error[50];
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parcels Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : !parcels || parcels.length === 0 ? (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {hasActiveFilters() ? 'No parcels match your filters' : 'No parcels found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasActiveFilters() 
              ? 'Try adjusting your search terms or filters to find what you\'re looking for'
              : activeTab === 'available' 
                ? 'Check back later for new parcels'
                : 'Create your first parcel to get started'
            }
          </p>
          {hasActiveFilters() && (
            <div className="mt-4 space-y-2">
              <button
                onClick={() => setFilters({ 
                  category: '', 
                  deliveryType: '',
                  minWeight: '', 
                  maxWeight: '', 
                  minValue: '', 
                  maxValue: '',
                  status: ''
                })}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </button>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Search
                </button>
              )}
            </div>
          )}
          {activeTab !== 'available' && !hasActiveFilters() && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Parcel
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(parcels) ? parcels : []).map((parcel) => {
            return <ParcelCardSleek 
                key={parcel.id}
                parcel={parcel}
                // onApply={handleApplyClick}
                onEdit={handleEditClick}
                onCancel={handleCancelClick}
                onDelete={handleDeleteClick}
                isOwner={user?.id === parcel.sender.id}
                showActions={activeTab === 'my'} />;
          })}
        </div>
      )}

      {/* Pagination */}
      {(totalItems > 0 || true) && ( // Temporarily always show for testing
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: colors.text.secondary }}>
            <span>Showing {startIndex}-{endIndex} of {totalItems} parcels</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className="px-2 py-1 rounded border text-sm"
              style={{ 
                borderColor: colors.border,
                backgroundColor: colors.neutral[50],
                color: colors.text.primary
              }}
            >
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
            </select>
          </div>

          {/* Page navigation - only show if multiple pages */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === 1 ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === 1 ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'text-white'
                          : 'text-secondary hover:text-primary'
                      }`}
                      style={{
                        backgroundColor: currentPage === pageNum ? colors.primary[600] : 'transparent',
                        color: currentPage === pageNum ? 'white' : colors.text.secondary
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.backgroundColor = colors.neutral[100];
                          e.currentTarget.style.color = colors.primary[600];
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = currentPage === pageNum ? colors.primary[600] : 'transparent';
                        e.currentTarget.style.color = currentPage === pageNum ? 'white' : colors.text.secondary;
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === totalPages ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === totalPages ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Parcel Modal */}
      {showCreateModal && (
        <CreateParcelModal
          onClose={() => {
            setShowCreateModal(false);
            setRepostData(null);
          }}
          onSubmit={handleCreateParcel}
          isLoading={createParcelMutation.isLoading}
          repostData={repostData}
        />
      )}

      {/* Edit Parcel Modal */}
      {showEditModal && selectedParcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-theme-light">
              <h2 className="text-xl font-semibold text-theme-primary">Edit Parcel</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedParcel(null);
                }}
                className="text-theme-secondary hover:text-theme-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <EditParcelForm
                parcel={selectedParcel}
                onSubmit={confirmEditParcel}
                onCancel={() => {
                  setShowEditModal(false);
                  setSelectedParcel(null);
                }}
                isLoading={editParcelMutation.isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedParcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-theme-card rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-theme-error-100 rounded-full flex items-center justify-center">
                <X className="h-6 w-6 text-theme-error" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-theme-primary">Cancel Parcel</h3>
                <p className="text-sm text-theme-secondary">
                  Are you sure you want to cancel this parcel? This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="bg-theme-neutral-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-theme-primary capitalize">
                {selectedParcel.category} parcel
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedParcel.pickup_city} → {selectedParcel.dropoff_city}
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedParcel.weight_kg}kg
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedParcel(null);
                }}
                className="px-4 py-2 text-theme-secondary bg-theme-neutral-100 rounded-md hover:bg-theme-neutral-200"
              >
                Keep Parcel
              </button>
              <button
                onClick={confirmCancelParcel}
                disabled={cancelParcelMutation.isLoading}
                className="px-4 py-2 bg-theme-error text-theme-inverse rounded-md hover:bg-theme-error-hover disabled:opacity-50"
              >
                {cancelParcelMutation.isLoading ? 'Cancelling...' : 'Cancel Parcel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Confirmation Modal */}
      {showApplyModal && selectedParcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            {/* <div className="bg-theme-card rounded-lg p-6 w-full max-w-md mx-4"> */}
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-theme-primary-100 rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-theme-primary" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-theme-primary">Apply to Deliver Parcel</h3>
                <p className="text-sm text-theme-secondary">
                  {selectedParcel.category} parcel from {selectedParcel.pickup} to {selectedParcel.dropoff}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-theme-neutral-50 rounded-lg p-4">
                <h4 className="font-medium text-theme-primary mb-2">Application Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: colors.text.secondary }}>Weight:</span>
                    <span style={{ color: colors.text.primary }}>{selectedParcel.weight_kg} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: colors.text.secondary }}>
                      {selectedParcel.purchased ? 'Purchase Price:' : 'Purchased:'}
                    </span>
                    <span style={{ color: colors.text.primary }}>
                      {selectedParcel.purchased 
                        ? (selectedParcel.purchase_price ? `ETB ${selectedParcel.purchase_price}` : 'N/A')
                        : (selectedParcel.purchased ? 'Yes' : 'No')
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: colors.text.secondary }}>
                      {selectedParcel.guaranteed_delivery ? 'Declared Value:' : 'Guaranteed Delivery:'}
                    </span>
                    <span style={{ color: colors.text.primary }}>
                      {selectedParcel.guaranteed_delivery 
                        ? (selectedParcel.declared_value ? `ETB ${selectedParcel.declared_value}` : 'N/A')
                        : (selectedParcel.guaranteed_delivery ? 'Yes' : 'No')
                      }
                    </span>
                  </div>
                  {/* {selectedParcel.guaranteed_delivery !== null && (
                    <div className="flex justify-between">
                      <span style={{ color: colors.text.secondary }}>Guaranteed Delivery:</span>
                      <span style={{ color: selectedParcel.guaranteed_delivery ? colors.success[600] : colors.error[600] }}>{selectedParcel.guaranteed_delivery ? 'Yes' : 'No'}</span>
                    </div>
                  )} */}
                </div>
              </div>
              
              <div className="bg-theme-warning-50 rounded-lg p-4">
                <h4 className="font-medium text-theme-primary mb-2">Application Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-theme-primary mb-1">
                      Your Delivery Price (ETB) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={bidPrice}
                        onChange={(e) => setBidPrice(e.target.value)}
                        max={selectedParcel.max_delivery_price}
                        min={50}
                        step="1"
                        className="w-full px-3 py-2 pr-16 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                        placeholder={`Up to ETB ${selectedParcel.max_delivery_price}`}
                      />
                      <div className="absolute right-3 top-2.5 flex items-center text-sm text-theme-secondary">
                        <DollarSign className="h-4 w-4" />
                      </div>
                    </div>
                    <p className="text-xs text-theme-muted mt-1">
                      Maximum allowed: ETB {selectedParcel.max_delivery_price}
                    </p>
                  </div>
                  
                  <div className="bg-theme-neutral-50 rounded-lg p-3">
                    <h5 className="font-medium text-theme-primary mb-2"> Competition Info</h5>
                    <div className="space-y-2 text-sm text-theme-secondary">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-success rounded-full mr-2"></div>
                        <span>Competitive bidding - Best price wins!</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-info rounded-full mr-2"></div>
                        <span>Lower bids increase your chances</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-warning rounded-full mr-2"></div>
                        <span>Sender reviews all applications</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-theme-warning-200 pt-3 mt-3">
                  <h5 className="font-medium text-theme-primary mb-2"> Fee Breakdown</h5>
                  {isLoadingFee ? (
                    <div className="text-sm text-theme-secondary">Loading fee information...</div>
                  ) : feeEstimate ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-theme-secondary">Application Fee:</span>
                        <span className="font-medium text-theme-primary">
                          ETB {feeEstimate.application_fee || 0}
                        </span>
                      </div>
                      {feeEstimate.deposit_requirement !== null && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-theme-secondary">Deposit Required:</span>
                          <span className="font-medium text-theme-primary">
                            ETB {feeEstimate.deposit_requirement}
                          </span>
                        </div>
                      )}
                      {feeEstimate.proposal_price !== null && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-theme-secondary">Delivery Fee:</span>
                          <span className="font-medium text-theme-primary">
                            ETB {feeEstimate.proposal_price}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-theme-warning-200 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-theme-primary">Total Required:</span>
                          <span className="font-bold text-theme-primary">
                            ETB {(feeEstimate.application_fee || 0) + (feeEstimate.deposit_requirement || 0) + (feeEstimate.proposal_price || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-theme-secondary">
                      Unable to load fee information. Please try again.
                    </p>
                  )}
                  <p className="text-xs text-theme-secondary mt-2">
                    Application fee is non-refundable. Deposit will be held until delivery completion.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowApplyModal(false);
                  setSelectedParcel(null);
                }}
                className="px-4 py-2 text-theme-secondary bg-theme-neutral-100 rounded-md hover:bg-theme-neutral-200"
              >
                Cancel
              </button>
              {(!isApplying && !isLoadingFee && bidPrice && parseFloat(bidPrice) >= 1 && parseFloat(bidPrice) <= selectedParcel.max_delivery_price) ? (
                <button
                  disabled={isLoadingFee || isApplying || !bidPrice || parseFloat(bidPrice) < 1 || parseFloat(bidPrice) > selectedParcel.max_delivery_price}
                  onClick={confirmApplyToParcel}
                  className="px-4 py-2 bg-theme-primary text-white rounded-md hover:bg-theme-primary-hover"
                >
                  {isApplying ? 'Submitting...' : 'Confirm Application'}
                </button>
              ) : (
                <button
                  disabled={isLoadingFee || isApplying}
                  className="px-4 py-2 bg-theme-neutral-300 text-theme-neutral-500 rounded-md cursor-not-allowed"
                >
                  Loading...
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedParcel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-theme-card rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-theme-error-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-theme-error" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-theme-primary">Delete Parcel</h3>
                <p className="text-sm text-theme-secondary">
                  Are you sure you want to delete this parcel? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-theme-neutral-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-theme-primary capitalize">
                {selectedParcel.category} parcel
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedParcel.pickup_city} → {selectedParcel.dropoff_city}
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedParcel.weight_kg}kg
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedParcel(null);
                }}
                className="px-4 py-2 text-theme-secondary bg-theme-neutral-100 rounded-md hover:bg-theme-neutral-200"
              >
                Keep Parcel
              </button>
              <button
                onClick={confirmDeleteParcel}
                disabled={deleteParcelMutation.isLoading}
                className="px-4 py-2 bg-theme-error text-theme-inverse rounded-md hover:bg-theme-error-hover disabled:opacity-50"
              >
                {deleteParcelMutation.isLoading ? 'Deleting...' : 'Delete Parcel'}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Parcels;
